<script lang="ts" setup>
import SignatureAlt from '@docue/docue-ui-v2/icons/SignatureAlt.vue'
import LineShieldTick from '@docue/docue-ui-v2/icons/LineShieldTick.vue'
import LineLightning01 from '@docue/docue-ui-v2/icons/LineLightning01.vue'

import snapImageUrl from '~/assets/images/snap.svg?url'

const uid = getCurrentInstance()?.uid

const { $posthog } = useNuxtApp()
const { t } = useI18n()
const { country } = useAuth()
const { translateForCountry } = useCountryAwareTranslation()
const { terms, privacyPolicy } = useLinks()

const { data: storyBlokConfig } = useQueryStoryblok.getAppConfig({
  country,
})

const {
  showIntercom,
  notifyContactFormTracker,
  paymentPeriodUnit,
  availablePaymentPeriodUnits,
  priceList,
} = useSubscriptionProcessContext()

const isPriceListV2 = computed(() => priceList.data.value?.data_version === 'v2')
const isPriceListV1_1 = computed(() => priceList.data.value?.data_version === 'v1.1')

const salesPeopleImageSource = computed(() => storyBlokConfig.value?.story.content.subscribe_modal_people?.filename)

const hasMultiplePaymentPeriodsAvailable = computed(() => (availablePaymentPeriodUnits.value ?? []).length > 1)

const paymentPeriodOptions = computed(() => ([
  {
    period: 'year',
    title: isPriceListV1_1.value ? t('subscribe.period.annual.title-v1-1') : t('subscribe.period.annual.title'),
    description: isPriceListV1_1.value ? t('subscribe.period.annual.description-v1-1') : t('subscribe.period.annual.description'),
    isChecked: paymentPeriodUnit.value === 'year',
    onChange: () => (paymentPeriodUnit.value = 'year'),
  },
  {
    period: 'month',
    title: isPriceListV1_1.value ? t('subscribe.period.monthly.title-v1-1') : t('subscribe.period.monthly.title'),
    description: isPriceListV1_1.value ? t('subscribe.period.monthly.description-v1-1') : t('subscribe.period.monthly.description'),
    isChecked: paymentPeriodUnit.value === 'month',
    onChange: () => (paymentPeriodUnit.value = 'month'),
  },
]))

const iconTextContentForBottom = computed(() => [
  {
    icon: LineLightning01,
    text: translateForCountry('subscribe.create'),
  },
  {
    icon: SignatureAlt,
    text: t('subscribe.plans.signing'),
  },
  {
    icon: LineShieldTick,
    text: t('subscribe.plans.store'),
  },
])

const isContactUsModalOpen = ref(false)

const termTitle = computed(() =>
  isPriceListV2.value || isPriceListV1_1.value
    ? translateForCountry('subscribe.subscriptionTermTitle')
    : t('subscribe.plans.terms.title'),
)

const termRenewalTextPath = computed(() =>
  isPriceListV2.value || isPriceListV1_1.value
    ? translateForCountry({ key: 'subscribe.subscriptionRenewalTermTitle', pathOnly: true })
    : 'subscribe.plans.terms.renewal-text',
)
</script>

<template>
  <LazyContactUsModal
    v-if="isContactUsModalOpen"
    v-model="isContactUsModalOpen"
    @notify-child-app-tracker="notifyContactFormTracker"
  />
  <div class="flex max-h-full flex-col overflow-y-auto overscroll-y-none">
    <div class="relative flex flex-col items-center gap-10 bg-cream">
      <div
        class="
            mb-72
            me-auto
            ms-auto
            flex
            w-full
            max-w-full
            flex-col
            items-center
            gap-10
            px-4
            md:w-[893px]
            "
      >
        <div
          class="mt-14 flex flex-col gap-5 text-pretty text-center"
        >
          <span class="text-3xl font-bold md:text-5xl">
            {{ translateForCountry('subscribe.plans-title') }}
          </span>
          <i18n-t
            :keypath="translateForCountry({ key: 'subscribe.plans-sub-title', pathOnly: true })"
            class="text-lg text-gray-600"
            tag="span"
          >
            <template #templatesLink>
              <DTButton
                :to="{ name: '2-templates-all' }"
                variant="link"
                dense
                class="!text-lg !text-gray-800"
                target="_blank"
                @click="() => $posthog.capture('template_list_opened')"
              >
                {{ translateForCountry('subscribe.templates-link') }}
              </DTButton>
            </template>
          </i18n-t>
        </div>

        <div
          v-if="hasMultiplePaymentPeriodsAvailable && !isPriceListV2"
          class="z-10 grid w-fit max-w-full auto-cols-max grid-cols-2 gap-2 rounded-xl bg-[#F5EDE8] p-1"
        >
          <label
            v-for="opts in paymentPeriodOptions"
            :key="opts.period"
            class="
                flex
                flex-1
                cursor-pointer
                select-none
                flex-col
                rounded-lg
                px-3.5
                py-2
                text-center
                ring-1
                ring-transparent
                focus-within:ring-gray-800
                "
            :class="opts.isChecked ? 'bg-white' : 'hover:bg-white/50'"
          >
            <span class="text-sm font-medium">{{ opts.title }}</span>
            <span class="text-xs text-gray-600">{{ opts.description }}</span>
            <input
              :checked="opts.isChecked"
              :value="opts.period"
              :name="`payment_period-${uid}`"
              type="radio"
              class="sr-only"
              @change="opts.onChange"
            >
          </label>
        </div>
      </div>

      <img
        :src="snapImageUrl"
        class="pointer-events-none absolute left-0 top-1/2 hidden select-none xl:inline"
      >
    </div>

    <div
      class="mb-20 me-auto ms-auto flex w-full max-w-full flex-col gap-20 px-4 md:w-[893px]"
    >
      <div
        class="flex flex-col items-center gap-6"
      >
        <div
          class="relative -mt-60 w-full rounded-xl"
        >
          <slot />
        </div>

        <div class="text-pretty">
          <p class="mb-6 text-sm font-medium empty:hidden">
            {{ termTitle }}
          </p>

          <i18n-t
            :keypath="termRenewalTextPath"
            tag="p"
            class="mb-3 text-xs text-gray-600"
          >
            <template #terms>
              <DTButton
                variant="link"
                size="xs"
                dense
                :to="terms"
                external
                target="_blank"
              >
                {{ t('terms-of-service') }}
              </DTButton>
            </template>
            <template #privacy>
              <DTButton
                variant="link"
                size="xs"
                dense
                :to="privacyPolicy"
                external
                target="_blank"
              >
                {{ t('privacy-policy') }}
              </DTButton>
            </template>
          </i18n-t>

          <p class="text-xs text-gray-600">
            {{ t('subscribe.plans.terms.templates-text') }}
          </p>
        </div>
      </div>

      <SubscriptionRefLogos />

      <div class="flex flex-col gap-10 text-center">
        <img
          v-if="salesPeopleImageSource"
          :src="salesPeopleImageSource"
          class="me-auto ms-auto w-64"
        >

        <div class="space-y-6">
          <p class="text-2xl font-bold md:text-4xl">
            {{ t('subscribe.plans.contact.title') }}
          </p>
          <p class="text-pretty text-lg text-gray-500">
            {{ t('subscribe.plans.contact.text') }}
          </p>
        </div>

        <div
          class="flex flex-col items-center justify-center gap-4 md:flex-row"
        >
          <DTButton
            variant="blue"
            @click="isContactUsModalOpen = true"
          >
            {{ t('subscribe.plans.contact.cta-message') }}
          </DTButton>
          <span class="text-sm text-gray-500">{{ t('or') }}</span>
          <DTButton @click="showIntercom">
            {{ t('subscribe.plans.contact.cta-chat') }}
          </DTButton>
        </div>
      </div>
    </div>

    <div class="bg-blue-50">
      <div
        class="
            my-20
            me-auto
            ms-auto
            flex
            w-full
            max-w-full
            flex-col
            items-center
            justify-around
            gap-5
            text-pretty
            text-center
            text-sm
            md:w-[893px]
            md:flex-row
            md:items-start
            "
      >
        <div
          v-for="({ icon, text }, index) in iconTextContentForBottom"
          :key="index"
          class="flex max-w-60 flex-col items-center gap-5"
        >
          <div
            class="flex size-14 items-center justify-center rounded-full bg-blue-100"
          >
            <component
              :is="icon"
              class="size-6 text-blue-700"
            />
          </div>
          <p>
            {{ text }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
